// contexts/MediaContext.js
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import startChimeMp3 from "../assets/bg/start-chime.mp3";
import startChimeMp4 from "../assets/bg/start-chime.mp4";

const MediaContext = createContext();

export const useMedia = () => {
  const ctx = useContext(MediaContext);
  if (!ctx) throw new Error('useMedia must be used within a MediaProvider');
  return ctx;
};

const SILENT_MP3 = startChimeMp3;
const SILENT_MP4 = startChimeMp4;

/** Evita que play() bloquee el arranque si el navegador no resuelve la promesa. */
function playWithTimeout(mediaEl, ms = 1200) {
  if (!mediaEl?.play) return Promise.resolve();
  return Promise.race([
    mediaEl.play(),
    new Promise((resolve) => setTimeout(resolve, ms)),
  ]).catch(() => {});
}

export const MediaProvider = ({ children }) => {
  const [isMediaUnlocked, setIsMediaUnlocked] = useState(false);

  const unlockAllMedia = useCallback(async () => {
    if (isMediaUnlocked) return true;

    try {

      // 1) WebAudio: resume AudioContext (iOS requiere gesto de usuario)
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (AC) {
          const ac = new AC();
          await ac.resume();
          // Disparo un beep cero (buffer vacío) para “tocar” la salida
          const src = ac.createBufferSource();
          src.buffer = ac.createBuffer(1, 1, 22050);
          src.connect(ac.destination);
          src.start(0);
          src.stop(0);
        }
      } catch (e) {
        // no pasa nada si falla
      }

      // 2) <audio> silencioso en DOM
      const aud = document.createElement('audio');
      aud.src = SILENT_MP3;
      aud.setAttribute('preload', 'auto');
      aud.style.position = 'fixed';
      aud.style.left = '-9999px';
      document.body.appendChild(aud);
      try {
        await playWithTimeout(aud);
        aud.pause();
      } catch (_) { /* ignore */ }
      aud.remove();

      // 3) <video> silencioso en DOM, playsInline
      const vid = document.createElement('video');
      vid.src = SILENT_MP4;
      vid.setAttribute('playsinline', '');
      vid.muted = true;        // importante: iOS permite autoplay muted
      vid.volume = 0;
      vid.loop = false;
      vid.preload = 'auto';
      vid.style.position = 'fixed';
      vid.style.left = '-9999px';
      document.body.appendChild(vid);
      try {
        await playWithTimeout(vid);
        vid.pause();
      } catch (_) { /* ignore */ }
      vid.remove();

      // Si hemos llegado hasta aquí, damos por desbloqueado
      setIsMediaUnlocked(true);

      // Intenta desmutear vídeos y audios ya en pantalla (sin bloquear el arranque)
      const videos = document.querySelectorAll('video');
      for (const v of videos) {
        try {
          v.setAttribute('playsinline', '');
          v.muted = false;
          v.volume = Math.max(v.volume || 0.8, 0.01);
          if (v.paused) void playWithTimeout(v, 800);
        } catch (e) {
          // en iOS puede requerir otro intento, pero ya tenemos el contexto desbloqueado
        }
      }

      const audios = document.querySelectorAll('audio[data-er-autoplay="1"]');
      for (const a of audios) {
        try {
          if (a.paused && a.src) void playWithTimeout(a, 800);
        } catch (e) {
          /* ignore */
        }
      }

      return true;
    } catch (error) {
      console.error('Error unlocking media:', error);
      return false;
    }
  }, [isMediaUnlocked]);

  // Fallback: si el usuario toca la pantalla en cualquier punto y aún no está desbloqueado, intenta de nuevo
  useEffect(() => {
    if (isMediaUnlocked) return;
    const handler = () => unlockAllMedia();
    window.addEventListener('touchend', handler, { once: true, passive: true });
    window.addEventListener('click', handler, { once: true });
    return () => {
      window.removeEventListener('touchend', handler);
      window.removeEventListener('click', handler);
    };
  }, [isMediaUnlocked, unlockAllMedia]);

  return (
    <MediaContext.Provider value={{ isMediaUnlocked, unlockAllMedia }}>
      {children}
    </MediaContext.Provider>
  );
};
